import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Layout, Skeleton, Typography } from "antd";
import {
  ReloadOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { fetchData } from "../api/apiService";
import { EMBED } from "../api/endpointMap";
import { EmbedTokenProvider, useEmbedToken } from "../context/EmbedTokenContext";
import { useOrgUsers } from "../context/OrgUsersContext";
import { unwrap } from "../pages/admin/utils";
import NoticeAILogo from "../assets/images/LoadingLogo.png";
import { avatarInitial } from "../utils/orgLabel";

const { Header, Content } = Layout;
const { Text } = Typography;

// ---------------------------------------------------------------------------
// Feature flag — embed header style.
//   true  → ProductionStyleHeader (NABS logo + NoticeAI logo + Team + Logout)
//           — visual parity with the regular tenant Navbar.
//   false → BrandingHeader (original Module 11 §3.3 partner-branded shell)
//
// Flip this single line to roll back without touching component code.
// ---------------------------------------------------------------------------
const USE_PRODUCTION_STYLE_HEADER = true;

// Module 11 — embed-mode context flag.
//
// Tenant pages that mount BOTH inside the regular tenant app (under
// `/dashboard`, `/income-tax/*` etc.) and inside the iframe (under
// `/embed/*`) read this to suppress write-flow / billing surfaces.
// The default is { isEmbed: false, readOnly: false } — outside an
// `EmbedLayout` tree, every existing tenant flow keeps its current
// behaviour byte-for-byte.
//
// See IFRAME_REUSE_IMPLEMENTATION_GUIDE.md §5.1 for the mounting
// contract and §6 for the suppress-or-keep table.
const EmbedModeContext = createContext({ isEmbed: false, readOnly: false });
export const useEmbedMode = () => useContext(EmbedModeContext);

// Module 11 §3.3 — strictly tenant-facing iframe routes. Branding shell
// only: small partner-coloured header (logo + display name + optional
// "Powered by NoticeAI"); no sidebar, no top-nav, no user dropdown.
// The parent ZOHO frame owns navigation chrome.

const DEFAULT_BRANDING = {
  logo_url: "",
  primary_color: "#1890ff",
  company_name: "NoticeAI",
  powered_by_visible: false,
};

// Admin-only "Team" affordance lives in the BrandingHeader (per
// 2026-05-04 decision — Option A: mount /embed/team + give admins a
// discoverable entry without restoring the full top-nav).
//
// Visibility ladder:
//   - role not in {tenant_admin, super_admin} → no link.
//   - admin + isSingleUserOrg=true → no link (lone admin doesn't
//     need a Team affordance until they invite #2; matches the
//     non-embed Navbar's same gate).
//   - admin + isSingleUserOrg=false → link visible.
//
// We call ensureLoaded() on admin-mount so isSingleUserOrg resolves
// to a real boolean before the conditional fires (avoids a flash-of-
// link on multi-user orgs whose context is still warming up). Cost:
// one extra GET /users on every embed page-load for admins, which
// the OrgUsersContext memoises across the iframe session anyway.
const TENANT_ADMIN_ROLES = new Set(["tenant_admin", "super_admin"]);

const AdminTeamLink = ({ role }) => {
  // Hidden in the embed iframe for ALL roles (user / admin / sub-admin /
  // tenant_admin / super_admin) — team management is the partner system's
  // responsibility inside the embed flow, not NoticeAI's. Direct (non-
  // embed) NoticeAI tenants still see the Users link via the regular
  // <Navbar /> path. To restore (e.g. if a partner explicitly opts into
  // letting their users edit team membership from NoticeAI), delete this
  // single return-null line — the original role + multi-user-org gates
  // are kept below intentionally so the rollback is one-line.
  return null;

  // eslint-disable-next-line no-unreachable
  // Original role-gated rendering preserved for easy rollback:
  // const navigate = useNavigate();
  // const { isSingleUserOrg, ensureLoaded } = useOrgUsers();
  // const isAdmin = TENANT_ADMIN_ROLES.has(role);
  // useEffect(() => {
  //   if (!isAdmin) return;
  //   ensureLoaded();
  // }, [isAdmin, ensureLoaded]);
  // if (!isAdmin) return null;
  // if (isSingleUserOrg !== false) return null;
  // return (
  //   <Button
  //     type="link"
  //     size="small"
  //     icon={<UsergroupAddOutlined />}
  //     onClick={() => navigate("/embed/team")}
  //     style={{ lineHeight: "1.5" }}
  //   >
  //     Users
  //   </Button>
  // );
};

// Production-style header (USE_PRODUCTION_STYLE_HEADER=true).
//
// Visually matches the regular tenant <Navbar /> — NABS logo on left,
// NoticeAI logo center, Team link on right. Header is fixed/sticky to
// the top of the iframe (mirrors Navbar.jsx:255 `fixed w-full top-0 z-50`).
// Plan badge + Logout omitted per user spec (Option A): plan needs
// AuthContext which is empty in embed; Logout doesn't apply because
// tokens are in-memory + parent owns session lifecycle.
//
// Rollback: flip USE_PRODUCTION_STYLE_HEADER → false at top of this file.
export const PRODUCTION_HEADER_HEIGHT = 72;
const ProductionStyleHeader = ({ role }) => {
  return (
    <Header
      className="flex items-center px-6"
      style={{
        background: "#fff",
        borderBottom: "1px solid #e6e9ef",
        height: PRODUCTION_HEADER_HEIGHT,
        lineHeight: `${PRODUCTION_HEADER_HEIGHT}px`,
        // Fixed-to-top — matches production Navbar (`fixed w-full top-0
        // z-50`). Content below adds `paddingTop` equal to the header
        // height so the page body doesn't get hidden underneath.
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}
    >
      {/* Left — empty spacer keeps NoticeAI centered (NABS logo
          intentionally removed; layout preserved). */}
      <div className="flex items-center" style={{ width: 32 }} />

      {/* Center — NoticeAI logo */}
      <div className="flex-1 flex items-center justify-center">
        <img
          src={NoticeAILogo}
          alt="NoticeAI"
          style={{ height: 32, objectFit: "contain" }}
          draggable={false}
        />
      </div>

      {/* Right — Team link only. Subscription badge intentionally
          omitted in embed mode: the partner workspace surfaces
          subscription state on its own dashboard, and showing the same
          chip again inside the embedded shell is noise the partner
          can't act on. The SubscriptionDropdown is also gated out of
          the inner NoticeAI navbar via `useIsEmbedMode()` for the same
          reason — both mount points are now consistent. */}
      <div className="flex items-center gap-3">
        <AdminTeamLink role={role} />
      </div>
    </Header>
  );
};

const BrandingHeader = ({ branding, role }) => {
  const b = branding || DEFAULT_BRANDING;
  return (
    <Header
      className="flex items-center px-4"
      style={{
        background: "#fff",
        borderBottom: `2px solid ${b.primary_color || "#1890ff"}`,
        height: 56,
        lineHeight: "56px",
      }}
    >
      <div className="flex items-center gap-3">
        {b.logo_url ? (
          <img
            src={b.logo_url}
            alt={b.company_name}
            style={{ height: 32, maxWidth: 140, objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div
            className="rounded flex items-center justify-center text-white font-semibold"
            style={{
              width: 32,
              height: 32,
              background: b.primary_color || "#1890ff",
            }}
          >
            {avatarInitial(b.company_name)}
          </div>
        )}
        <Text strong style={{ fontSize: 16 }}>
          {(b.company_name || "").trim() || "NoticeAI"}
        </Text>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <AdminTeamLink role={role} />
        {b.powered_by_visible && (
          <Text type="secondary" className="text-xs">
            Powered by NoticeAI
          </Text>
        )}
      </div>
    </Header>
  );
};

// Inner shell — runs inside EmbedTokenProvider so it can read claims.
const EmbedShellInner = () => {
  const {
    token,
    claims,
    ready,
    tokenExpired,
    partnerId,
    notifyTokenExpired,
  } = useEmbedToken();
  const [branding, setBranding] = useState(null);
  const location = useLocation();

  // Broadcast inner-iframe route changes to the parent (partner) frame so
  // it can mirror the iframe's current page into its OWN outer URL. The
  // outer URL is the only thing that survives a browser refresh — the
  // iframe's internal React Router state is wiped on reload — so without
  // this sync, refreshing while on a detail page lands the user back on
  // the list page (the outer URL still said "list"). We strip the embed
  // JWT from the search before broadcasting; tokens are short-lived and
  // partner-side EmbedFrame mints a fresh one on every mount.
  useEffect(() => {
    if (window.parent === window) return undefined;
    try {
      const cleaned = new URLSearchParams(location.search);
      cleaned.delete("token");
      cleaned.delete("from");
      window.parent.postMessage(
        {
          type: "noticeai.route_changed",
          pathname: location.pathname,
          search: cleaned.toString() ? `?${cleaned.toString()}` : "",
        },
        "*",
      );
    } catch {
      /* cross-origin can theoretically block postMessage; safe to swallow */
    }
    return undefined;
  }, [location.pathname, location.search]);

  // Fetch branding once we know the partner_id. Uses public endpoint —
  // no auth required, returns NoticeAI defaults if partner not found.
  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    fetchData(EMBED.BRANDING(partnerId))
      .then((raw) => {
        if (cancelled) return;
        const u = unwrap(raw) || {};
        setBranding(u.branding || u || DEFAULT_BRANDING);
      })
      .catch(() => {
        if (cancelled) return;
        setBranding(DEFAULT_BRANDING);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  // Push partner primary-color into a CSS variable on the document root
  // so AntD components (buttons, links, focused inputs) pick it up
  // automatically without per-component branding logic. Per spec §5.2.
  // Only runs inside the embed tree — tenant pages outside don't see
  // this var because EmbedLayout is the only writer.
  useEffect(() => {
    const color = branding?.primary_color;
    if (!color) return undefined;
    const root = document.documentElement;
    const previous = root.style.getPropertyValue("--ant-primary-color");
    root.style.setProperty("--ant-primary-color", color);
    // Restore on unmount so navigating away from /embed/* doesn't
    // leave the override stuck on a tenant-app session loaded in the
    // same tab afterwards.
    return () => {
      if (previous) {
        root.style.setProperty("--ant-primary-color", previous);
      } else {
        root.style.removeProperty("--ant-primary-color");
      }
    };
  }, [branding?.primary_color]);

  // Defence-in-depth route guard. The actual route tree below only
  // mounts the four allowed pages, but if the parent app somehow ends
  // up at /embed/admin/foo (mistyped link, deep-link from old docs),
  // we render a hard 404 here rather than letting React Router fall
  // through to a sibling matcher.
  const FORBIDDEN_PREFIXES = [
    "/embed/admin",
    "/embed/partner-admin",
    "/embed/settings",
  ];
  const isForbidden = FORBIDDEN_PREFIXES.some((p) =>
    location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  // Pre-render gates. These MUST run before we mount the actual page —
  // a tenant API call without a token would 401, the interceptor would
  // postMessage the parent, and we'd loop forever rendering nothing.
  let body = null;
  if (!ready) {
    body = (
      <div className="p-8">
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  } else if (isForbidden) {
    body = (
      <div className="p-8 max-w-xl mx-auto">
        <Alert
          type="error"
          showIcon
          message="Not available"
          description="This route is not embeddable."
        />
      </div>
    );
  } else if (tokenExpired) {
    // Phase 3 §4.3 item 3 — auto-logout on token expiry. The
    // EmbedTokenProvider's `exp` timer already cleared axios's
    // in-memory token + flipped this flag. Render an opaque overlay
    // (no silent failure) prompting the user to close the iframe and
    // reopen it from the host. The Notify-host button posts the
    // expiry signal again in case the parent missed the original
    // 60s-warning postMessage.
    body = (
      <div className="p-8 max-w-xl mx-auto">
        <Alert
          type="warning"
          showIcon
          message="Session expired - close and reopen"
          description="Your session has timed out. Close this view in your host application and reopen it to start a fresh session."
          action={
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => notifyTokenExpired()}
            >
              Notify host
            </Button>
          }
        />
      </div>
    );
  } else if (!token) {
    body = (
      <div className="p-8 max-w-xl mx-auto">
        <Alert
          type="error"
          showIcon
          message="Session expired"
          description="Close and reopen this view from your host application to start a fresh session."
          action={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => notifyTokenExpired()}
            >
              Notify host
            </Button>
          }
        />
      </div>
    );
  } else if (!claims?.org_id && !claims?.organization_id) {
    body = (
      <div className="p-8 max-w-xl mx-auto">
        <Alert
          type="warning"
          showIcon
          message="Token missing tenant context"
          description="The session token doesn't carry an organization. Contact your host application."
        />
      </div>
    );
  } else {
    // Wrap the matched child route in EmbedModeContext so any tenant
    // page mounted under `/embed/*` (e.g. pages/Dashboard.jsx) reads
    // `{ isEmbed: true, readOnly: true }` and suppresses its
    // billing/upgrade surfaces. `readOnly` is currently the same
    // signal as `isEmbed` — kept as a separate field so future
    // surfaces can opt into a less-strict variant without a wire
    // break (e.g. partner-paid embeds where billing IS visible).
    body = (
      <EmbedModeContext.Provider value={{ isEmbed: true, readOnly: true }}>
        {/* Scoped style: kill the page-level Antd Breadcrumb (which
            also contains the "Home" / Dashboard link) for partner-iframe
            users so they can't navigate out of the assigned scope.
            Direct NoticeAI tenants still get the full breadcrumb because
            they don't render through this provider. */}
        <style>{`
          .noticeai-embed-root .ant-breadcrumb { display: none !important; }
        `}</style>
        <div className="noticeai-embed-root">
          <Outlet />
        </div>
      </EmbedModeContext.Provider>
    );
  }

  return (
    <Layout className="min-h-screen">
      {USE_PRODUCTION_STYLE_HEADER ? (
        <ProductionStyleHeader role={claims?.role} />
      ) : (
        <BrandingHeader branding={branding} role={claims?.role} />
      )}
      <Content
        style={{
          background: "#f5f7fa",
          // ProductionStyleHeader is fixed-positioned (off normal flow);
          // pad the content by exactly the header height so the page
          // body starts below it. BrandingHeader sits in normal flow
          // (height 56, line 137 below) so no padding needed there.
          paddingTop: USE_PRODUCTION_STYLE_HEADER
            ? PRODUCTION_HEADER_HEIGHT
            : 0,
        }}
      >
        {body}
      </Content>
    </Layout>
  );
};

// Public layout — wraps the inner shell in the token provider. Routed
// at `/embed` so all child routes inherit the bootstrap.
const EmbedLayout = () => (
  <EmbedTokenProvider>
    <EmbedShellInner />
  </EmbedTokenProvider>
);

// Not-found surface for unknown /embed/* sub-paths. Rendered inside
// EmbedLayout (so the partner branding shell still paints), distinct
// from the forbidden-prefix alert which has a tighter "not available"
// message for known-but-disallowed admin-ish paths.
export const EmbedNotFound = () => (
  <div className="p-8 max-w-xl mx-auto">
    <Alert
      type="info"
      showIcon
      message="Page not found"
      description="The host application linked to a page that doesn't exist in the embedded view."
    />
  </div>
);

export default EmbedLayout;
